import React, { useEffect, useRef, useState } from "react";
import { translateApiMessage } from "../utils/formErrors";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  CalendarDays,
  Camera,
  Loader2,
  LogOut,
  Mail,
  Phone,
  Save,
  Settings,
  UserRound,
} from "lucide-react";
import toast from "react-hot-toast";

import axiosInstance from "../utils/axiosInstance";
import { logout } from "../actions/authActions";

const initialForm = {
  email: "",
  username: "",
  first_name: "",
  last_name: "",
  phone_number: "",
  birth_date: "",
  role: "",
  picture: null,
};

const roleLabels = {
  admin: "مدیر",
  editor: "ویرایشگر",
  viewer: "مشاهده‌گر",
  accountent: "حسابدار",
  client: "کاربر",
};

const getErrorMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data?.detail === "string") return translateApiMessage(data.detail);
  if (data && typeof data === "object") {
    const value = Object.values(data)[0];
    if (Array.isArray(value)) return translateApiMessage(value[0]);
    if (typeof value === "string") return translateApiMessage(value);
  }
  return "ذخیره اطلاعات پروفایل ناموفق بود.";
};

const UserProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(initialForm);
  const [pictureUrl, setPictureUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  const fetchProfile = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await axiosInstance.get("/accounts/profile/");
      const profile = response.data;
      setForm({
        email: profile.email || "",
        username: profile.username || "",
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        phone_number: profile.phone_number || "",
        birth_date: profile.birth_date || "",
        role: profile.role || "",
        picture: null,
      });
      setPictureUrl(profile.picture || "");
    } catch (error) {
      setLoadError("دریافت اطلاعات پروفایل ناموفق بود.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(
    () => () => {
      if (pictureUrl?.startsWith("blob:")) URL.revokeObjectURL(pictureUrl);
    },
    [pictureUrl],
  );

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const updatePicture = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    setForm((current) => ({ ...current, picture: file }));
    setPictureUrl(URL.createObjectURL(file));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);

    const payload = new FormData();
    payload.append("username", form.username.trim());
    payload.append("first_name", form.first_name || "");
    payload.append("last_name", form.last_name || "");
    payload.append("phone_number", form.phone_number || "");
    if (form.birth_date) payload.append("birth_date", form.birth_date);
    if (form.picture instanceof File) payload.append("picture", form.picture);

    try {
      const response = await axiosInstance.patch(
        "/accounts/profile/update/",
        payload,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const profile = response.data;
      setForm((current) => ({
        ...current,
        email: profile.email || current.email,
        username: profile.username || "",
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        phone_number: profile.phone_number || "",
        birth_date: profile.birth_date || "",
        role: profile.role || current.role,
        picture: null,
      }));
      setPictureUrl(profile.picture || pictureUrl);

      try {
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...storedUser,
            username: profile.username,
            phone_number: profile.phone_number,
            birth_date: profile.birth_date,
          }),
        );
      } catch {
        // The API remains the source of truth if local storage is malformed.
      }

      toast.success("اطلاعات پروفایل ذخیره شد.");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const displayName =
    [form.first_name, form.last_name].filter(Boolean).join(" ") ||
    form.username ||
    "کاربر";

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50 pt-20">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 size={20} className="animate-spin" />
          در حال دریافت پروفایل
        </div>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4 pt-20">
        <div className="text-center">
          <p className="text-sm text-rose-700">{loadError}</p>
          <button
            type="button"
            onClick={fetchProfile}
            className="mt-4 rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700"
          >
            تلاش دوباره
          </button>
        </div>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-slate-50 px-3 pb-12 pt-24 font-vazir sm:px-6 lg:px-8"
      dir="rtl"
    >
      <div className="mx-auto max-w-6xl">
        <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-sky-700">
              <UserRound size={18} />
              حساب کاربری
            </div>
            <h1 className="text-2xl font-bold text-slate-950">پروفایل کاربر</h1>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              اطلاعات هویتی و راه‌های ارتباطی حساب خود را مدیریت کنید.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/user/settings"
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <Settings size={17} />
              تنظیمات
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700"
            >
              <LogOut size={17} />
              خروج
            </button>
          </div>
        </header>

        <form
          onSubmit={submit}
          className="mt-6 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
        >
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr]">
            <aside className="border-b border-slate-200 bg-slate-50 p-6 lg:border-b-0 lg:border-l">
              <div className="mx-auto h-36 w-36 overflow-hidden rounded-lg border border-slate-200 bg-white">
                {pictureUrl ? (
                  <img
                    src={pictureUrl}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="grid h-full w-full place-items-center">
                    <UserRound size={52} className="text-slate-300" />
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="mx-auto mt-3 flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                <Camera size={16} />
                تغییر تصویر
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={updatePicture}
                className="hidden"
              />

              <div className="mt-5 text-center">
                <p className="font-bold text-slate-900">{displayName}</p>
                <p className="mt-1 text-xs text-slate-500">{form.email}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700">
                  <BadgeCheck size={14} />
                  {roleLabels[form.role] || form.role}
                </span>
              </div>
            </aside>

            <section className="p-5 sm:p-7">
              <div className="mb-5">
                <h2 className="font-bold text-slate-900">اطلاعات حساب</h2>
                <p className="mt-1 text-xs text-slate-500">
                  ایمیل و نقش کاربری توسط مدیر سامانه کنترل می‌شوند.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <label>
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">
                    نام کاربری
                  </span>
                  <div className="relative">
                    <UserRound
                      size={17}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      required
                      maxLength={50}
                      name="username"
                      value={form.username}
                      onChange={updateField}
                      className="w-full rounded-md border border-slate-300 py-2.5 pl-3 pr-10 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                    />
                  </div>
                </label>

                <label>
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">
                    ایمیل
                  </span>
                  <div className="relative">
                    <Mail
                      size={17}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      readOnly
                      value={form.email}
                      className="w-full cursor-not-allowed rounded-md border border-slate-200 bg-slate-50 py-2.5 pl-3 pr-10 text-sm text-slate-500"
                    />
                  </div>
                </label>

                <label>
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">
                    نام
                  </span>
                  <input
                    maxLength={50}
                    name="first_name"
                    value={form.first_name}
                    onChange={updateField}
                    className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                  />
                </label>

                <label>
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">
                    نام خانوادگی
                  </span>
                  <input
                    maxLength={50}
                    name="last_name"
                    value={form.last_name}
                    onChange={updateField}
                    className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                  />
                </label>

                <label>
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">
                    شماره تماس
                  </span>
                  <div className="relative">
                    <Phone
                      size={17}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      maxLength={20}
                      name="phone_number"
                      value={form.phone_number}
                      onChange={updateField}
                      className="w-full rounded-md border border-slate-300 py-2.5 pl-3 pr-10 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                    />
                  </div>
                </label>

                <label>
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">
                    تاریخ تولد
                  </span>
                  <div className="relative">
                    <CalendarDays
                      size={17}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="date"
                      name="birth_date"
                      value={form.birth_date}
                      onChange={updateField}
                      className="w-full rounded-md border border-slate-300 py-2.5 pl-3 pr-10 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                    />
                  </div>
                </label>
              </div>
            </section>
          </div>

          <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-4 sm:px-7">
            <button
              type="submit"
              disabled={saving || !form.username.trim()}
              className="inline-flex min-w-36 items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? (
                <Loader2 size={17} className="animate-spin" />
              ) : (
                <Save size={17} />
              )}
              ذخیره تغییرات
            </button>
          </div>
        </form>
      </div>
    </main>
  );
};

export default UserProfile;
